import { db } from '../../services/firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit, 
  getCountFromServer,
  startAfter,
  Timestamp
} from 'firebase/firestore';

/**
 * Service de statistiques moderne utilisant les vraies données Firestore
 * Optimisé pour les performances avec mise en cache et requêtes efficaces
 */

/**
 * Vérifie si une erreur est liée aux permissions Firestore
 * @param {Error} error - L'erreur à vérifier
 * @returns {boolean} - True si c'est une erreur de permission
 */
const isPermissionError = (error) => {
  const errorCode = error?.code || '';
  const errorMessage = error?.message || '';
  
  return errorCode === 'permission-denied' ||
         errorCode === 'unauthenticated' ||
         errorCode === 'unavailable' ||
         errorMessage.includes('permission-denied') ||
         errorMessage.includes('Missing or insufficient permissions') ||
         errorMessage.includes('Could not reach Cloud Firestore backend');
};

/**
 * Gère les erreurs de permissions de manière gracieuse
 * @param {Error} error - L'erreur à gérer
 * @param {string} operation - Le nom de l'opération qui a échoué
 * @param {any} fallbackValue - Valeur par défaut à retourner
 * @returns {any} - La valeur de fallback
 */
const handlePermissionError = (error, operation, fallbackValue = null) => {
  if (isPermissionError(error)) {

    return fallbackValue;
  }
  // Si ce n'est pas une erreur de permission, la relancer
  throw error;
};

// Cache pour optimiser les performances
const cache = {
  data: null,
  timestamp: 0,
  CACHE_DURATION: 5 * 60 * 1000 // 5 minutes
};

/**
 * Vérifie si les données en cache sont encore valides
 */
const isCacheValid = () => {
  return cache.data && (Date.now() - cache.timestamp) < cache.CACHE_DURATION;
};

/**
 * Met en cache les données avec timestamp
 */
const setCache = (data) => {
  cache.data = data;
  cache.timestamp = Date.now();
};

/**
 * Récupère les statistiques complètes de l'application
 * @returns {Promise<object>} Statistiques complètes
 */
export const getApplicationStats = async () => {
  try {
    // Vérifier le cache d'abord
    if (isCacheValid()) {
      return cache.data;
    }

    // Exécuter toutes les requêtes en parallèle pour optimiser les performances
    const [
      usersCount,
      meetingsCount,
      transcriptionsCount,
      summariesCount,
      recentMeetingsData,
      monthlyActivityData,
      transcriptionsByService,
      userActivityData
    ] = await Promise.all([
      getUsersCount(),
      getMeetingsCount(),
      getTranscriptionsCount(),
      getSummariesCount(),
      getRecentMeetings(),
      getMonthlyActivity(),
      getTranscriptionsByService(),
      getUserActivity()
    ]);

    // Calculer les métriques avancées
    const averageDuration = await calculateAverageAudioDuration();
    const successRate = calculateTranscriptionSuccessRate(transcriptionsCount);
    const topUsers = getTopActiveUsers(userActivityData);

    const stats = {
      // Métriques de base
      totalUsers: usersCount,
      totalMeetings: meetingsCount,
      totalTranscriptions: transcriptionsCount.total,
      totalSummaries: summariesCount,

      // Métriques par service
      transcriptionsByService,

      // Métriques avancées
      averageAudioDurationInSeconds: averageDuration,
      transcriptionSuccessRate: successRate,
      
      // Données temporelles
      monthlyActivity: monthlyActivityData,
      recentMeetings: recentMeetingsData,
      
      // Utilisateurs les plus actifs
      topUsers,

      // Métriques de performance
      metrics: {
        avgTranscriptionsPerUser: Math.round((transcriptionsCount.total / usersCount) * 100) / 100,
        avgMeetingsPerUser: Math.round((meetingsCount / usersCount) * 100) / 100,
        summaryGenerationRate: Math.round((summariesCount / transcriptionsCount.total) * 100) / 100
      },

      // Timestamp de génération
      generatedAt: new Date(),
      cacheExpiry: new Date(Date.now() + cache.CACHE_DURATION)
    };

    // Mettre en cache
    setCache(stats);

    return stats;

  } catch (error) {

    // Essayer la méthode alternative si les agrégations échouent
    if (isPermissionError(error)) {
      try {
        const alternativeStats = await getStatsWithLimitedQueries();
        return alternativeStats;
      } catch (alternativeError) {

      }
    }
    
    // Retourner des données par défaut en cas d'erreur complète
    return {
      totalUsers: 0,
      totalMeetings: 0,
      totalTranscriptions: 0,
      totalSummaries: 0,
      transcriptionsByService: { openAI: 0, assemblyAI: 0 },
      averageAudioDurationInSeconds: 0,
      transcriptionSuccessRate: 0,
      monthlyActivity: [],
      recentMeetings: [],
      topUsers: [],
      metrics: {
        avgTranscriptionsPerUser: 0,
        avgMeetingsPerUser: 0,
        summaryGenerationRate: 0
      },
      error: error.message,
      fallbackUsed: true,
      generatedAt: new Date()
    };
  }
};

/**
 * Compte le nombre total d'utilisateurs actifs
 */
const getUsersCount = async () => {
  try {
    const usersCollection = collection(db, 'users');
    const usersSnapshot = await getCountFromServer(usersCollection);
    return usersSnapshot.data().count;
  } catch (error) {

    return 0;
  }
};

/**
 * Compte le nombre total de réunions
 */
const getMeetingsCount = async () => {
  try {
    const meetingsCollection = collection(db, 'meetings');
    const meetingsSnapshot = await getCountFromServer(meetingsCollection);
    return meetingsSnapshot.data().count;
  } catch (error) {

    return 0;
  }
};

/**
 * Compte les transcriptions par statut et service
 */
const getTranscriptionsCount = async () => {
  try {
    const transcriptionsCollection = collection(db, 'transcriptions');
    
    // Compter toutes les transcriptions
    const totalSnapshot = await getCountFromServer(transcriptionsCollection);
    
    // Compter par statut
    const completedQuery = query(transcriptionsCollection, where('status', '==', 'completed'));
    const completedSnapshot = await getCountFromServer(completedQuery);
    
    const errorQuery = query(transcriptionsCollection, where('status', '==', 'error'));
    const errorSnapshot = await getCountFromServer(errorQuery);

    return {
      total: totalSnapshot.data().count,
      completed: completedSnapshot.data().count,
      errors: errorSnapshot.data().count
    };
  } catch (error) {

    return { total: 0, completed: 0, errors: 0 };
  }
};

/**
 * Compte le nombre total de résumés
 */
const getSummariesCount = async () => {
  try {
    const summariesCollection = collection(db, 'summaries');
    const summariesSnapshot = await getCountFromServer(summariesCollection);
    return summariesSnapshot.data().count;
  } catch (error) {

    return 0;
  }
};

/**
 * Récupère les transcriptions par service (OpenAI vs AssemblyAI)
 */
const getTranscriptionsByService = async () => {
  try {
    const transcriptionsCollection = collection(db, 'transcriptions');
    
    // Compter par service
    const openAIQuery = query(transcriptionsCollection, where('provider', '==', 'openai'));
    const assemblyAIQuery = query(transcriptionsCollection, where('provider', '==', 'assemblyai'));
    
    const [openAISnapshot, assemblyAISnapshot] = await Promise.all([
      getCountFromServer(openAIQuery),
      getCountFromServer(assemblyAIQuery)
    ]);

    return {
      openAI: openAISnapshot.data().count,
      assemblyAI: assemblyAISnapshot.data().count
    };
  } catch (error) {

    return { openAI: 0, assemblyAI: 0 };
  }
};

/**
 * Récupère les 10 dernières réunions avec leurs détails
 */
const getRecentMeetings = async () => {
  try {
    const meetingsCollection = collection(db, 'meetings');
    const recentQuery = query(
      meetingsCollection,
      orderBy('createdAt', 'desc'),
      limit(10)
    );
    
    const querySnapshot = await getDocs(recentQuery);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title || 'Réunion sans titre',
        status: data.status || 'pending',
        createdAt: data.createdAt?.toDate() || new Date(),
        userId: data.userId,
        userName: data.userName || null,
        userEmail: data.userEmail || null,
        duration: data.duration || null,
        transcriptionId: data.transcriptionId || null
      };
    });
  } catch (error) {

    return [];
  }
};

/**
 * Calcule la durée moyenne des enregistrements audio
 */
const calculateAverageAudioDuration = async () => {
  try {
    const transcriptionsCollection = collection(db, 'transcriptions');
    const transcriptionsQuery = query(
      transcriptionsCollection,
      where('duration', '>', 0),
      limit(100) // Limiter pour optimiser les performances
    );
    
    const querySnapshot = await getDocs(transcriptionsQuery);
    
    if (querySnapshot.empty) return 0;
    
    let totalDuration = 0;
    let count = 0;
    
    querySnapshot.forEach(doc => {
      const duration = doc.data().duration;
      if (duration && duration > 0) {
        totalDuration += duration;
        count++;
      }
    });
    
    return count > 0 ? Math.round(totalDuration / count) : 0;
  } catch (error) {

    return 0;
  }
};

/**
 * Calcule le taux de succès des transcriptions
 */
const calculateTranscriptionSuccessRate = (transcriptionStats) => {
  if (!transcriptionStats || transcriptionStats.total === 0) return 0;
  
  return Math.round((transcriptionStats.completed / transcriptionStats.total) * 100 * 100) / 100;
};

/**
 * Récupère l'activité des utilisateurs pour identifier les plus actifs
 */
const getUserActivity = async () => {
  try {
    const meetingsCollection = collection(db, 'meetings');
    const usersCollection = collection(db, 'users');
    
    const meetingsQuery = query(
      meetingsCollection,
      orderBy('createdAt', 'desc'),
      limit(200) // Analyser les 200 dernières réunions
    );
    
    const [meetingsSnapshot, usersSnapshot] = await Promise.all([
      getDocs(meetingsQuery),
      getDocs(usersCollection)
    ]);
    
    // Créer un map des utilisateurs pour un accès rapide
    const usersMap = new Map();
    usersSnapshot.forEach(doc => {
      const userData = doc.data();
      const displayName = userData.displayName || userData.firstName || userData.email?.split('@')[0] || 'Utilisateur';
      const lastName = userData.lastName || '';
      const fullName = lastName ? `${displayName} ${lastName}` : displayName;
      
      usersMap.set(doc.id, {
        displayName: fullName,
        email: userData.email,
        isAdmin: userData.isAdmin || false
      });
    });
    
    const userActivity = new Map();
    
    meetingsSnapshot.forEach(doc => {
      const data = doc.data();
      const userId = data.userId;
      
      if (userId) {
        const userInfo = usersMap.get(userId);
        const userName = userInfo?.displayName || data.userName || data.userEmail || `Utilisateur ${userId.substring(0, 8)}`;
        
        const current = userActivity.get(userId) || {
          userId,
          userName,
          userEmail: userInfo?.email || data.userEmail,
          isAdmin: userInfo?.isAdmin || false,
          meetings: 0,
          transcriptions: 0,
          lastActivity: null
        };
        
        current.meetings++;
        
        if (data.transcriptionId) {
          current.transcriptions++;
        }
        
        const createdAt = data.createdAt?.toDate();
        if (createdAt && (!current.lastActivity || createdAt > current.lastActivity)) {
          current.lastActivity = createdAt;
        }
        
        userActivity.set(userId, current);
      }
    });
    
    return Array.from(userActivity.values());
  } catch (error) {

    return [];
  }
};

/**
 * Extrait les top 5 utilisateurs les plus actifs
 */
const getTopActiveUsers = (userActivityData) => {
  return userActivityData
    .sort((a, b) => (b.meetings + b.transcriptions) - (a.meetings + a.transcriptions))
    .slice(0, 5)
    .map(user => ({
      name: user.userName,
      email: user.userEmail,
      isAdmin: user.isAdmin,
      meetings: user.meetings,
      transcriptions: user.transcriptions,
      totalActivity: user.meetings + user.transcriptions,
      lastActivity: user.lastActivity
    }));
};

/**
 * Récupère l'activité mensuelle des 6 derniers mois
 */
const getMonthlyActivity = async () => {
  try {
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    
    const meetingsCollection = collection(db, 'meetings');
    const transcriptionsCollection = collection(db, 'transcriptions');
    
    // Requêtes pour les 6 derniers mois
    const meetingsQuery = query(
      meetingsCollection,
      where('createdAt', '>=', Timestamp.fromDate(sixMonthsAgo)),
      orderBy('createdAt', 'asc')
    );
    
    const transcriptionsQuery = query(
      transcriptionsCollection,
      where('createdAt', '>=', Timestamp.fromDate(sixMonthsAgo)),
      orderBy('createdAt', 'asc')
    );
    
    const [meetingsSnapshot, transcriptionsSnapshot] = await Promise.all([
      getDocs(meetingsQuery),
      getDocs(transcriptionsQuery)
    ]);
    
    // Créer la structure des 6 derniers mois
    const monthlyData = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthNames = [
        'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
        'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'
      ];
      
      monthlyData.push({
        month: `${monthNames[date.getMonth()]} ${date.getFullYear()}`,
        meetings: 0,
        transcriptions: 0,
        year: date.getFullYear(),
        monthIndex: date.getMonth()
      });
    }
    
    // Compter les réunions par mois
    meetingsSnapshot.forEach(doc => {
      const createdAt = doc.data().createdAt?.toDate();
      if (createdAt) {
        const year = createdAt.getFullYear();
        const month = createdAt.getMonth();
        
        const monthData = monthlyData.find(m => m.year === year && m.monthIndex === month);
        if (monthData) {
          monthData.meetings++;
        }
      }
    });
    
    // Compter les transcriptions par mois
    transcriptionsSnapshot.forEach(doc => {
      const createdAt = doc.data().createdAt?.toDate();
      if (createdAt) {
        const year = createdAt.getFullYear();
        const month = createdAt.getMonth();
        
        const monthData = monthlyData.find(m => m.year === year && m.monthIndex === month);
        if (monthData) {
          monthData.transcriptions++;
        }
      }
    });
    
    // Nettoyer les données (retirer year et monthIndex)
    return monthlyData.map(({ year, monthIndex, ...data }) => data);
    
  } catch (error) {

    // Retourner des données vides par défaut
    const monthlyData = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthNames = [
        'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
        'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'
      ];
      
      monthlyData.push({
        month: `${monthNames[date.getMonth()]} ${date.getFullYear()}`,
        meetings: 0,
        transcriptions: 0
      });
    }
    
    return monthlyData;
  }
};

/**
 * Invalide le cache (utile pour forcer un rechargement)
 */
export const invalidateStatsCache = () => {
  cache.data = null;
  cache.timestamp = 0;
};

/**
 * Récupère les statistiques de coûts (si disponible)
 * Cette fonction peut être étendue pour intégrer avec les functions Firebase
 */
export const getCostStatistics = async () => {
  try {
    // Pour l'instant, on retourne des données vides
    // Cette fonction peut être étendue pour intégrer avec les Firebase Functions
    return {
      totalCost: 0,
      monthlyCost: 0,
      costByService: {
        openAI: 0,
        assemblyAI: 0
      },
      costTrend: []
    };
  } catch (error) {

    return null;
  }
};

/**
 * Récupère les statistiques limitées pour les utilisateurs non-admin
 * N'utilise que des requêtes autorisées (pas d'agrégation globale)
 * @param {string} userId - ID de l'utilisateur
 * @returns {Promise<object>} Statistiques limitées
 */
export const getUserStats = async (userId) => {
  try {
    if (!userId) {
      throw new Error('ID utilisateur requis');
    }

    // Récupérer les données limitées de l'utilisateur (autorisées par les règles)
    const [userMeetingsData, userTranscriptionsData, userSummariesData] = await Promise.all([
      getUserMeetingsData(userId).catch(err => {

        return [];
      }),
      getUserTranscriptionsData(userId).catch(err => {

        return [];
      }),
      getUserSummariesData(userId).catch(err => {

        return [];
      })
    ]);
    
    const stats = {
      // Statistiques utilisateur uniquement
      userMeetings: userMeetingsData.length,
      userTranscriptions: userTranscriptionsData.length,
      userSummaries: userSummariesData.length,
      
      // Dernières activités
      recentMeetings: userMeetingsData.slice(0, 5),
      
      // Pas de statistiques globales pour les non-admin
      isLimitedView: true,
      
      // Timestamp de génération
      generatedAt: new Date()
    };
    
    return stats;
    
  } catch (error) {

    // Retourner des données par défaut en cas d'erreur
    return {
      userMeetings: 0,
      userTranscriptions: 0,
      userSummaries: 0,
      recentMeetings: [],
      isLimitedView: true,
      error: error.message,
      generatedAt: new Date()
    };
  }
};

/**
 * Récupère les meetings de l'utilisateur (autorisé par les règles)
 */
const getUserMeetingsData = async (userId) => {
  try {
    const meetingsCollection = collection(db, 'meetings');
    const userMeetingsQuery = query(
      meetingsCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    
    const querySnapshot = await getDocs(userMeetingsQuery);
    const meetings = [];
    
    querySnapshot.forEach(doc => {
      meetings.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return meetings;
  } catch (error) {
    return handlePermissionError(error, 'récupération des meetings utilisateur', []);
  }
};

/**
 * Récupère les transcriptions de l'utilisateur (autorisé par les règles)
 */
const getUserTranscriptionsData = async (userId) => {
  try {
    const transcriptionsCollection = collection(db, 'transcriptions');
    const userTranscriptionsQuery = query(
      transcriptionsCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    
    const querySnapshot = await getDocs(userTranscriptionsQuery);
    const transcriptions = [];
    
    querySnapshot.forEach(doc => {
      transcriptions.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return transcriptions;
  } catch (error) {
    return handlePermissionError(error, 'récupération des transcriptions utilisateur', []);
  }
};

/**
 * Récupère les résumés de l'utilisateur (autorisé par les règles)
 */
const getUserSummariesData = async (userId) => {
  try {
    const summariesCollection = collection(db, 'summaries');
    const userSummariesQuery = query(
      summariesCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    
    const querySnapshot = await getDocs(userSummariesQuery);
    const summaries = [];
    
    querySnapshot.forEach(doc => {
      summaries.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return summaries;
  } catch (error) {
    return handlePermissionError(error, 'récupération des résumés utilisateur', []);
  }
};

/**
 * Alternative robuste aux requêtes d'agrégation 
 * Utilise des requêtes limitées et extrapolation pour éviter les problèmes de permissions
 */
const getStatsWithLimitedQueries = async () => {
  try {
    
    // Récupérer des échantillons de données avec des requêtes limitées
    const [usersSample, meetingsSample, transcriptionsSample, summariesSample] = await Promise.all([
      getUsersSample(),
      getMeetingsSample(), 
      getTranscriptionsSample(),
      getSummariesSample()
    ]);
    
    // Calculer les statistiques basées sur les échantillons
    const stats = {
      // Métriques de base (basées sur les échantillons disponibles)
      totalUsers: usersSample.length,
      totalMeetings: meetingsSample.length,
      totalTranscriptions: transcriptionsSample.length,
      totalSummaries: summariesSample.length,

      // Métriques par service
      transcriptionsByService: calculateServiceBreakdown(transcriptionsSample),

      // Métriques avancées
      averageAudioDurationInSeconds: calculateAverageDuration(transcriptionsSample),
      transcriptionSuccessRate: calculateSuccessRate(transcriptionsSample),
      
      // Données temporelles
      monthlyActivity: calculateMonthlyActivity(meetingsSample, transcriptionsSample),
      recentMeetings: meetingsSample.slice(0, 10),
      
      // Utilisateurs les plus actifs
      topUsers: calculateTopUsers(meetingsSample),

      // Métriques de performance
      metrics: {
        avgTranscriptionsPerUser: usersSample.length > 0 ? Math.round((transcriptionsSample.length / usersSample.length) * 100) / 100 : 0,
        avgMeetingsPerUser: usersSample.length > 0 ? Math.round((meetingsSample.length / usersSample.length) * 100) / 100 : 0,
        summaryGenerationRate: transcriptionsSample.length > 0 ? Math.round((summariesSample.length / transcriptionsSample.length) * 100) / 100 : 0
      },

      // Indicateurs de limitation
      isLimitedData: true,
      sampleSize: {
        users: usersSample.length,
        meetings: meetingsSample.length,
        transcriptions: transcriptionsSample.length,
        summaries: summariesSample.length
      },

      // Timestamp de génération
      generatedAt: new Date(),
      cacheExpiry: new Date(Date.now() + cache.CACHE_DURATION)
    };

    return stats;

  } catch (error) {

    throw error;
  }
};

/**
 * Récupère un échantillon d'utilisateurs
 */
const getUsersSample = async () => {
  try {
    const usersCollection = collection(db, 'users');
    const usersQuery = query(usersCollection, limit(100));
    
    const querySnapshot = await getDocs(usersQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {

    return [];
  }
};

/**
 * Récupère un échantillon de meetings
 */
const getMeetingsSample = async () => {
  try {
    const meetingsCollection = collection(db, 'meetings');
    const meetingsQuery = query(
      meetingsCollection,
      orderBy('createdAt', 'desc'),
      limit(200)
    );
    
    const querySnapshot = await getDocs(meetingsQuery);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        title: data.title || 'Réunion sans titre',
        status: data.status || 'pending',
        createdAt: data.createdAt?.toDate() || new Date(),
        userId: data.userId,
        userName: data.userName || null,
        userEmail: data.userEmail || null,
        duration: data.duration || null,
        transcriptionId: data.transcriptionId || null
      };
    });
  } catch (error) {

    return [];
  }
};

/**
 * Récupère un échantillon de transcriptions
 */
const getTranscriptionsSample = async () => {
  try {
    const transcriptionsCollection = collection(db, 'transcriptions');
    const transcriptionsQuery = query(
      transcriptionsCollection,
      orderBy('createdAt', 'desc'),
      limit(200)
    );
    
    const querySnapshot = await getDocs(transcriptionsQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {

    return [];
  }
};

/**
 * Récupère un échantillon de résumés
 */
const getSummariesSample = async () => {
  try {
    const summariesCollection = collection(db, 'summaries');
    const summariesQuery = query(
      summariesCollection,
      orderBy('createdAt', 'desc'),
      limit(200)
    );
    
    const querySnapshot = await getDocs(summariesQuery);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {

    return [];
  }
};

/**
 * Calcule la répartition par service
 */
const calculateServiceBreakdown = (transcriptions) => {
  const breakdown = { openAI: 0, assemblyAI: 0 };
  
  transcriptions.forEach(t => {
    if (t.provider === 'openai') breakdown.openAI++;
    else if (t.provider === 'assemblyai') breakdown.assemblyAI++;
  });
  
  return breakdown;
};

/**
 * Calcule la durée moyenne
 */
const calculateAverageDuration = (transcriptions) => {
  const validDurations = transcriptions
    .filter(t => t.duration && t.duration > 0)
    .map(t => t.duration);
  
  if (validDurations.length === 0) return 0;
  
  const total = validDurations.reduce((sum, duration) => sum + duration, 0);
  return Math.round(total / validDurations.length);
};

/**
 * Calcule le taux de succès
 */
const calculateSuccessRate = (transcriptions) => {
  if (transcriptions.length === 0) return 0;
  
  const completed = transcriptions.filter(t => t.status === 'completed').length;
  return Math.round((completed / transcriptions.length) * 100 * 100) / 100;
};

/**
 * Calcule l'activité mensuelle
 */
const calculateMonthlyActivity = (meetings, transcriptions) => {
  const now = new Date();
  const monthlyData = [];
  
  // Créer la structure des 6 derniers mois
  for (let i = 5; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthNames = [
      'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
      'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'
    ];
    
    monthlyData.push({
      month: `${monthNames[date.getMonth()]} ${date.getFullYear()}`,
      meetings: 0,
      transcriptions: 0,
      year: date.getFullYear(),
      monthIndex: date.getMonth()
    });
  }
  
  // Compter les meetings par mois
  meetings.forEach(meeting => {
    const createdAt = meeting.createdAt;
    if (createdAt) {
      const year = createdAt.getFullYear();
      const month = createdAt.getMonth();
      
      const monthData = monthlyData.find(m => m.year === year && m.monthIndex === month);
      if (monthData) {
        monthData.meetings++;
      }
    }
  });
  
  // Compter les transcriptions par mois
  transcriptions.forEach(transcription => {
    const createdAt = transcription.createdAt?.toDate ? transcription.createdAt.toDate() : transcription.createdAt;
    if (createdAt) {
      const year = createdAt.getFullYear();
      const month = createdAt.getMonth();
      
      const monthData = monthlyData.find(m => m.year === year && m.monthIndex === month);
      if (monthData) {
        monthData.transcriptions++;
      }
    }
  });
  
  // Nettoyer les données
  return monthlyData.map(({ year, monthIndex, ...data }) => data);
};

/**
 * Calcule les utilisateurs les plus actifs
 */
const calculateTopUsers = (meetings) => {
  const userActivity = new Map();
  
  meetings.forEach(meeting => {
    const userId = meeting.userId;
    const userName = meeting.userName || meeting.userEmail || `Utilisateur ${userId?.substring(0, 8) || 'Inconnu'}`;
    
    if (userId) {
      const current = userActivity.get(userId) || {
        userId,
        userName,
        userEmail: meeting.userEmail,
        isAdmin: false,
        meetings: 0,
        transcriptions: 0,
        lastActivity: null
      };
      
      current.meetings++;
      
      if (meeting.transcriptionId) {
        current.transcriptions++;
      }
      
      if (meeting.createdAt && (!current.lastActivity || meeting.createdAt > current.lastActivity)) {
        current.lastActivity = meeting.createdAt;
      }
      
      userActivity.set(userId, current);
    }
  });
  
  return Array.from(userActivity.values())
    .sort((a, b) => (b.meetings + b.transcriptions) - (a.meetings + a.transcriptions))
    .slice(0, 5)
    .map(user => ({
      name: user.userName,
      email: user.userEmail,
      isAdmin: user.isAdmin,
      meetings: user.meetings,
      transcriptions: user.transcriptions,
      totalActivity: user.meetings + user.transcriptions,
      lastActivity: user.lastActivity
    }));
};

/**
 * Export de la méthode alternative pour les statistiques
 * Utile pour les tests ou en cas de problèmes avec les agrégations
 */
export const getAlternativeStats = getStatsWithLimitedQueries;

// Export des fonctions utilitaires pour les tests
export const utils = {
  isCacheValid,
  setCache,
  calculateTranscriptionSuccessRate,
  getTopActiveUsers,
  isPermissionError,
  handlePermissionError,
  getStatsWithLimitedQueries
}; 